import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { User, Mail, Lock, Save, AlertCircle, CheckCircle, Building2, Edit2, ArrowLeft, Trash2, CreditCard, Calendar, Clock, Download, FileText, Shield } from 'lucide-react';
import { supabase, getUserId } from '../lib/supabase';
import { AdditionalServices } from './AdditionalServices';

interface UserProfile {
  first_name: string;
  last_name: string;
  company: string;
  trial_ends_at: string | null;
  fiscal_year_start_month: number;
  fiscal_year_start_day: number;
  fiscal_year_end_month: number;
  fiscal_year_end_day: number;
  two_factor_enabled: boolean;
  two_factor_method: string;
}

interface Subscription {
  status: string;
  plan_type: string;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  current_period_end: string | null;
}

interface Invoice {
  id: string;
  number: string | null;
  amount: number;
  currency: string;
  status: string;
  created: number;
  invoice_pdf: string | null;
  hosted_invoice_url: string | null;
  paid: boolean;
}

interface AccountTabProps {
  onBackToDashboard?: () => void;
}

export function AccountTab({ onBackToDashboard }: AccountTabProps) {
  const { user, effectiveUserId } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [company, setCompany] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [profileError, setProfileError] = useState('');
  const [success, setSuccess] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editingFiscalYear, setEditingFiscalYear] = useState(false);
  const [fiscalYearMonth, setFiscalYearMonth] = useState(9);
  const [fiscalYearDay, setFiscalYearDay] = useState(1);
  const [fiscalYearEndMonth, setFiscalYearEndMonth] = useState(8);
  const [fiscalYearEndDay, setFiscalYearEndDay] = useState(31);
  const [fiscalYearError, setFiscalYearError] = useState('');
  const [fiscalYearSuccess, setFiscalYearSuccess] = useState('');
  const [fiscalYearLoading, setFiscalYearLoading] = useState(false);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);
  const [twoFactorError, setTwoFactorError] = useState('');
  const [twoFactorSuccess, setTwoFactorSuccess] = useState('');

  useEffect(() => {
    loadProfile();
    loadSubscription();
  }, [user]);

  useEffect(() => {
    if (subscription) {
      loadInvoices();
    }
  }, [subscription]);

  async function loadProfile() {
    if (!user) return;

    const { data } = await supabase
      .from('user_profiles')
      .select('first_name, last_name, company, trial_ends_at, fiscal_year_start_month, fiscal_year_start_day, fiscal_year_end_month, fiscal_year_end_day, two_factor_enabled, two_factor_method')
      .eq('id', user.id)
      .maybeSingle();

    if (data) {
      setProfile(data);
      setFirstName(data.first_name);
      setLastName(data.last_name);
      setCompany(data.company);
      setFiscalYearMonth(data.fiscal_year_start_month || 9);
      setFiscalYearDay(data.fiscal_year_start_day || 1);
      setFiscalYearEndMonth(data.fiscal_year_end_month || 8);
      setFiscalYearEndDay(data.fiscal_year_end_day || 31);
      setTwoFactorEnabled(data.two_factor_enabled || false);
    }
  }

  async function loadSubscription() {
    if (!user) return;

    const { data } = await supabase
      .from('subscriptions')
      .select('status, plan_type, stripe_customer_id, stripe_subscription_id, current_period_end')
      .eq('user_id', user.id)
      .maybeSingle();

    if (data) {
      setSubscription(data);
    }
  }

  async function loadInvoices() {
    if (!user) return;

    setInvoicesLoading(true);
    try {
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-stripe-invoice?user_id=${user.id}`;
      const headers = {
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json',
      };

      const response = await fetch(apiUrl, { headers });
      const data = await response.json();

      if (data.invoices) {
        setInvoices(data.invoices);
      }
    } catch (error) {
      console.error('Error loading invoices:', error);
    } finally {
      setInvoicesLoading(false);
    }
  }

  async function handleProfileUpdate(e: React.FormEvent) {
    e.preventDefault();
    setProfileError('');
    setProfileSuccess('');

    if (!firstName || !lastName || !company) {
      setProfileError('Veuillez remplir tous les champs');
      return;
    }

    setProfileLoading(true);

    const { error: updateError } = await supabase
      .from('user_profiles')
      .update({
        first_name: firstName,
        last_name: lastName,
        company: company,
      })
      .eq('id', user?.id);

    if (updateError) {
      setProfileError(updateError.message);
    } else {
      setProfileSuccess('Profil mis à jour avec succès');
      setProfile({ ...profile, first_name: firstName, last_name: lastName, company });
      setEditingProfile(false);
    }

    setProfileLoading(false);
  }

  async function handleTwoFactorToggle() {
    if (!user) return;

    setTwoFactorLoading(true);
    setTwoFactorError('');
    setTwoFactorSuccess('');

    try {
      const newValue = !twoFactorEnabled;

      const { error } = await supabase
        .from('user_profiles')
        .update({ two_factor_enabled: newValue })
        .eq('id', user.id);

      if (error) throw error;

      setTwoFactorEnabled(newValue);
      setTwoFactorSuccess(
        newValue
          ? 'Authentification à deux facteurs activée avec succès'
          : 'Authentification à deux facteurs désactivée'
      );

      setTimeout(() => setTwoFactorSuccess(''), 3000);
    } catch (error) {
      console.error('Error toggling 2FA:', error);
      setTwoFactorError('Erreur lors de la modification des paramètres 2FA');
    } finally {
      setTwoFactorLoading(false);
    }
  }

  async function handlePasswordChange(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!newPassword || !confirmPassword) {
      setError('Veuillez remplir tous les champs');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    if (newPassword.length < 6) {
      setError('Le nouveau mot de passe doit contenir au moins 6 caractères');
      return;
    }

    setLoading(true);

    const { error: updateError } = await supabase.auth.updateUser({
      password: newPassword
    });

    if (updateError) {
      setError(updateError.message);
    } else {
      setSuccess('Mot de passe modifié avec succès');
      setNewPassword('');
      setConfirmPassword('');
    }

    setLoading(false);
  }

  async function handleDeleteTestData() {
    setDeleteLoading(true);
    setError('');
    setSuccess('');

    try {
      const userId = await getUserId();

      const { data: cashFlowLinesData } = await supabase
        .from('cash_flow_forecast_lines')
        .select('id')
        .or(`user_id.eq.${userId},user_id.is.null`);

      if (cashFlowLinesData && cashFlowLinesData.length > 0) {
        const { error: deleteCashFlowLinesError } = await supabase
          .from('cash_flow_forecast_lines')
          .delete()
          .in('id', cashFlowLinesData.map(line => line.id));

        if (deleteCashFlowLinesError) {
          console.error('Error deleting cash flow lines:', deleteCashFlowLinesError);
          throw deleteCashFlowLinesError;
        }
      }

      const { data: transactionsData } = await supabase
        .from('transactions')
        .select('id')
        .or(`user_id.eq.${userId},user_id.is.null`);

      if (transactionsData && transactionsData.length > 0) {
        const { error: deleteTransactionsError } = await supabase
          .from('transactions')
          .delete()
          .in('id', transactionsData.map(t => t.id));

        if (deleteTransactionsError) {
          console.error('Error deleting transactions:', deleteTransactionsError);
          throw deleteTransactionsError;
        }
      }

      const { data: budgetData } = await supabase
        .from('budget_lines')
        .select('id')
        .or(`user_id.eq.${userId},user_id.is.null`);

      if (budgetData && budgetData.length > 0) {
        const { error: deleteBudgetError } = await supabase
          .from('budget_lines')
          .delete()
          .in('id', budgetData.map(b => b.id));

        if (deleteBudgetError) {
          console.error('Error deleting budget lines:', deleteBudgetError);
          throw deleteBudgetError;
        }
      }

      const { data: scenariosData } = await supabase
        .from('scenarios')
        .select('id')
        .or(`user_id.eq.${userId},user_id.is.null`);

      if (scenariosData && scenariosData.length > 0) {
        const { error: deleteScenariosError } = await supabase
          .from('scenarios')
          .delete()
          .in('id', scenariosData.map(s => s.id));

        if (deleteScenariosError) {
          console.error('Error deleting scenarios:', deleteScenariosError);
          throw deleteScenariosError;
        }
      }

      const { data: cashFlowData } = await supabase
        .from('cash_flow_forecasts')
        .select('id')
        .or(`user_id.eq.${userId},user_id.is.null`);

      if (cashFlowData && cashFlowData.length > 0) {
        const { error: deleteCashFlowError } = await supabase
          .from('cash_flow_forecasts')
          .delete()
          .in('id', cashFlowData.map(cf => cf.id));

        if (deleteCashFlowError) {
          console.error('Error deleting cash flow forecasts:', deleteCashFlowError);
          throw deleteCashFlowError;
        }
      }

      setSuccess('Toutes les données ont été supprimées avec succès');
      setShowDeleteConfirm(false);

      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      setError('Erreur lors de la suppression des données');
      console.error('Error deleting data:', error);
    } finally {
      setDeleteLoading(false);
    }
  }

  async function handleFiscalYearUpdate(e: React.FormEvent) {
    e.preventDefault();
    setFiscalYearError('');
    setFiscalYearSuccess('');

    if (fiscalYearMonth < 1 || fiscalYearMonth > 12) {
      setFiscalYearError('Le mois de début doit être entre 1 et 12');
      return;
    }

    if (fiscalYearDay < 1 || fiscalYearDay > 31) {
      setFiscalYearError('Le jour de début doit être entre 1 et 31');
      return;
    }

    if (fiscalYearEndMonth < 1 || fiscalYearEndMonth > 12) {
      setFiscalYearError('Le mois de fin doit être entre 1 et 12');
      return;
    }

    if (fiscalYearEndDay < 1 || fiscalYearEndDay > 31) {
      setFiscalYearError('Le jour de fin doit être entre 1 et 31');
      return;
    }

    setFiscalYearLoading(true);

    const { error: updateError } = await supabase
      .from('user_profiles')
      .update({
        fiscal_year_start_month: fiscalYearMonth,
        fiscal_year_start_day: fiscalYearDay,
        fiscal_year_end_month: fiscalYearEndMonth,
        fiscal_year_end_day: fiscalYearEndDay,
      })
      .eq('id', user?.id);

    if (updateError) {
      setFiscalYearError(updateError.message);
    } else {
      setFiscalYearSuccess('Dates d\'exercice comptable mises à jour. Rechargement de la page...');
      if (profile) {
        setProfile({
          ...profile,
          fiscal_year_start_month: fiscalYearMonth,
          fiscal_year_start_day: fiscalYearDay,
          fiscal_year_end_month: fiscalYearEndMonth,
          fiscal_year_end_day: fiscalYearEndDay
        });
      }
      setEditingFiscalYear(false);

      setTimeout(() => {
        window.location.reload();
      }, 1500);
    }

    setFiscalYearLoading(false);
  }

  const monthNames = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {onBackToDashboard && (
        <button
          onClick={onBackToDashboard}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft size={20} />
          Retour au tableau de bord
        </button>
      )}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200">
          <div className="bg-blue-100 p-3 rounded-full">
            <User className="text-blue-600" size={24} />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Mon compte</h2>
            <p className="text-sm text-gray-600">Gérez vos informations personnelles</p>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <User className="text-gray-600" size={20} />
                <h3 className="text-lg font-semibold text-gray-900">Informations personnelles</h3>
              </div>
              {!editingProfile && (
                <button
                  onClick={() => setEditingProfile(true)}
                  className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors"
                >
                  <Edit2 size={16} />
                  Modifier
                </button>
              )}
            </div>

            {editingProfile ? (
              <form onSubmit={handleProfileUpdate} className="space-y-4">
                {profileError && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                    <AlertCircle size={18} />
                    {profileError}
                  </div>
                )}

                {profileSuccess && (
                  <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
                    <CheckCircle size={18} />
                    {profileSuccess}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Prénom</label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nom</label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Building2 size={16} className="inline mr-2" />
                    Société / Établissement
                  </label>
                  <input
                    type="text"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    type="submit"
                    disabled={profileLoading}
                    className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Save size={18} />
                    {profileLoading ? 'Enregistrement...' : 'Enregistrer'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingProfile(false);
                      if (profile) {
                        setFirstName(profile.first_name);
                        setLastName(profile.last_name);
                        setCompany(profile.company);
                      }
                      setProfileError('');
                      setProfileSuccess('');
                    }}
                    className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-all"
                  >
                    Annuler
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prénom</label>
                  <div className="px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900">
                    {profile?.first_name || 'Non renseigné'}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                  <div className="px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900">
                    {profile?.last_name || 'Non renseigné'}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Société / Établissement</label>
                  <div className="px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900">
                    {profile?.company || 'Non renseigné'}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-3">
              <Mail className="text-gray-600" size={20} />
              <h3 className="text-lg font-semibold text-gray-900">Informations du compte</h3>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <div className="px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900">
                  {user?.email}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date de création</label>
                <div className="px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900">
                  {user?.created_at ? new Date(user.created_at).toLocaleDateString('fr-FR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  }) : 'Non disponible'}
                </div>
              </div>
            </div>
          </div>

          {subscription && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-4">
                <CreditCard className="text-green-600" size={20} />
                <h3 className="text-lg font-semibold text-gray-900">Mon abonnement</h3>
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <label className="block text-sm font-medium text-gray-700 w-40">Statut</label>
                  <span
                    className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${
                      subscription.status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : subscription.status === 'suspended'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {subscription.status === 'active' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
                    {subscription.status === 'active' ? 'Actif' :
                     subscription.status === 'suspended' ? 'Suspendu' : 'Annulé'}
                  </span>
                </div>

                {subscription.plan_type && (
                  <div className="flex items-center gap-3">
                    <label className="block text-sm font-medium text-gray-700 w-40">Plan</label>
                    <div className="px-4 py-2 bg-white border border-green-300 rounded-lg text-gray-900 font-semibold">
                      {subscription.plan_type === 'yearly' ? 'Annuel' : subscription.plan_type === 'monthly' ? 'Mensuel' : subscription.plan_type}
                    </div>
                  </div>
                )}

                {subscription.current_period_end && subscription.status === 'active' && (
                  <div className="flex items-center gap-3">
                    <label className="block text-sm font-medium text-gray-700 w-40">Prochain renouvellement</label>
                    <div className="flex items-center gap-2 px-4 py-2 bg-white border border-green-300 rounded-lg text-gray-900">
                      <Calendar className="text-green-600" size={16} />
                      <span className="font-medium">
                        {new Date(subscription.current_period_end).toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        })}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {subscription && (
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-4">
                <FileText className="text-gray-600" size={20} />
                <h3 className="text-lg font-semibold text-gray-900">Mes factures</h3>
              </div>

              {invoicesLoading ? (
                <div className="text-center py-8 text-gray-600">
                  Chargement des factures...
                </div>
              ) : invoices.length === 0 ? (
                <div className="text-center py-8 text-gray-600">
                  Aucune facture disponible
                </div>
              ) : (
                <div className="space-y-2">
                  {invoices.map((invoice) => (
                    <div
                      key={invoice.id}
                      className="flex items-center justify-between p-4 bg-white border border-gray-300 rounded-lg hover:border-gray-400 transition-colors"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <span className="font-semibold text-gray-900">
                            {invoice.number || 'Facture sans numéro'}
                          </span>
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              invoice.paid
                                ? 'bg-green-100 text-green-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}
                          >
                            {invoice.paid ? 'Payée' : 'En attente'}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          <span>
                            {new Date(invoice.created * 1000).toLocaleDateString('fr-FR', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric'
                            })}
                          </span>
                          <span className="font-semibold text-gray-900">
                            {invoice.amount.toFixed(2)} {invoice.currency.toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {invoice.invoice_pdf && (
                          <a
                            href={invoice.invoice_pdf}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                          >
                            <Download size={16} />
                            Télécharger PDF
                          </a>
                        )}
                        {invoice.hosted_invoice_url && (
                          <a
                            href={invoice.hosted_invoice_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium"
                          >
                            <FileText size={16} />
                            Voir en ligne
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Calendar className="text-gray-600" size={20} />
                <h3 className="text-lg font-semibold text-gray-900">Exercice comptable</h3>
              </div>
              {!editingFiscalYear && (
                <button
                  onClick={() => setEditingFiscalYear(true)}
                  className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors"
                >
                  <Edit2 size={16} />
                  Modifier
                </button>
              )}
            </div>

            {editingFiscalYear ? (
              <form onSubmit={handleFiscalYearUpdate} className="space-y-4">
                {fiscalYearError && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                    <AlertCircle size={18} />
                    {fiscalYearError}
                  </div>
                )}

                {fiscalYearSuccess && (
                  <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
                    <CheckCircle size={18} />
                    {fiscalYearSuccess}
                  </div>
                )}

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    Définissez les dates de début et de fin de votre exercice comptable. Par défaut, l'exercice va du 1er septembre au 31 août (12 mois).
                    Tous vos tableaux de bord, rapports et indicateurs seront calculés en fonction de ces dates.
                  </p>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Date de début d'exercice</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Mois de début</label>
                      <select
                        value={fiscalYearMonth}
                        onChange={(e) => setFiscalYearMonth(Number(e.target.value))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {monthNames.map((month, index) => (
                          <option key={index + 1} value={index + 1}>
                            {month}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Jour de début</label>
                      <input
                        type="number"
                        min="1"
                        max="31"
                        value={fiscalYearDay}
                        onChange={(e) => setFiscalYearDay(Number(e.target.value))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Date de fin d'exercice</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Mois de fin</label>
                      <select
                        value={fiscalYearEndMonth}
                        onChange={(e) => setFiscalYearEndMonth(Number(e.target.value))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {monthNames.map((month, index) => (
                          <option key={index + 1} value={index + 1}>
                            {month}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Jour de fin</label>
                      <input
                        type="number"
                        min="1"
                        max="31"
                        value={fiscalYearEndDay}
                        onChange={(e) => setFiscalYearEndDay(Number(e.target.value))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    type="submit"
                    disabled={fiscalYearLoading}
                    className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Save size={18} />
                    {fiscalYearLoading ? 'Enregistrement...' : 'Enregistrer'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingFiscalYear(false);
                      if (profile) {
                        setFiscalYearMonth(profile.fiscal_year_start_month || 9);
                        setFiscalYearDay(profile.fiscal_year_start_day || 1);
                        setFiscalYearEndMonth(profile.fiscal_year_end_month || 8);
                        setFiscalYearEndDay(profile.fiscal_year_end_day || 31);
                      }
                      setFiscalYearError('');
                      setFiscalYearSuccess('');
                    }}
                    className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-all"
                  >
                    Annuler
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date de début d'exercice</label>
                  <div className="px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 font-medium">
                    {fiscalYearDay} {monthNames[fiscalYearMonth - 1]}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date de fin d'exercice</label>
                  <div className="px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 font-medium">
                    {fiscalYearEndDay} {monthNames[fiscalYearEndMonth - 1]}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Période</label>
                  <div className="px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-600 text-sm">
                    Du {fiscalYearDay} {monthNames[fiscalYearMonth - 1]} au {fiscalYearEndDay} {monthNames[fiscalYearEndMonth - 1]}
                  </div>
                </div>
              </div>
            )}
          </div>

          <AdditionalServices />

          {!subscription && profile?.trial_ends_at && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-4">
                <Clock className="text-blue-600" size={20} />
                <h3 className="text-lg font-semibold text-gray-900">Période d'essai</h3>
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <label className="block text-sm font-medium text-gray-700 w-40">Statut</label>
                  {new Date(profile.trial_ends_at) > new Date() ? (
                    <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                      <CheckCircle size={16} />
                      Essai actif
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                      <AlertCircle size={16} />
                      Essai expiré
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <label className="block text-sm font-medium text-gray-700 w-40">Date d'expiration</label>
                  <div className="px-4 py-2 bg-white border border-blue-300 rounded-lg text-gray-900">
                    {new Date(profile.trial_ends_at).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-4">
              <Lock className="text-gray-600" size={20} />
              <h3 className="text-lg font-semibold text-gray-900">Changer le mot de passe</h3>
            </div>

            <form onSubmit={handlePasswordChange} className="space-y-4">
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                  <AlertCircle size={18} />
                  {error}
                </div>
              )}

              {success && (
                <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
                  <CheckCircle size={18} />
                  {success}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nouveau mot de passe
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="••••••••"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirmer le nouveau mot de passe
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="••••••••"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save size={18} />
                {loading ? 'Enregistrement...' : 'Modifier le mot de passe'}
              </button>
            </form>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-4">
              <Shield className="text-gray-600" size={20} />
              <h3 className="text-lg font-semibold text-gray-900">Authentification à deux facteurs (2FA)</h3>
            </div>

            {twoFactorError && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm mb-4">
                <AlertCircle size={18} />
                {twoFactorError}
              </div>
            )}

            {twoFactorSuccess && (
              <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm mb-4">
                <CheckCircle size={18} />
                {twoFactorSuccess}
              </div>
            )}

            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Ajoutez une couche de sécurité supplémentaire à votre compte en activant
                l'authentification à deux facteurs. Un code de vérification sera envoyé à votre
                adresse email à chaque connexion.
              </p>

              <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-full ${twoFactorEnabled ? 'bg-green-100' : 'bg-gray-100'}`}>
                    <Shield className={twoFactorEnabled ? 'text-green-600' : 'text-gray-400'} size={20} />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {twoFactorEnabled ? 'Activé' : 'Désactivé'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {twoFactorEnabled
                        ? 'Votre compte est protégé par 2FA'
                        : 'Activez le 2FA pour plus de sécurité'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleTwoFactorToggle}
                  disabled={twoFactorLoading}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    twoFactorEnabled
                      ? 'bg-red-600 text-white hover:bg-red-700'
                      : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  {twoFactorLoading ? 'Chargement...' : twoFactorEnabled ? 'Désactiver' : 'Activer'}
                </button>
              </div>

              {twoFactorEnabled && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs text-blue-800">
                    Lors de votre prochaine connexion, un code de vérification à 6 chiffres sera
                    envoyé à votre adresse email <strong>{user?.email}</strong>
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
              <div>
                <h4 className="font-semibold text-blue-900 mb-1">Sécurité de votre compte</h4>
                <p className="text-sm text-blue-800">
                  Vos données sont protégées et isolées. Seul vous avez accès à vos transactions,
                  budgets et prévisions. Assurez-vous d'utiliser un mot de passe fort et unique.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-4">
              <Trash2 className="text-red-600" size={20} />
              <h3 className="text-lg font-semibold text-red-900">Zone dangereuse</h3>
            </div>

            {!showDeleteConfirm ? (
              <div>
                <p className="text-sm text-red-800 mb-4">
                  Supprimez toutes vos données (transactions, budgets, scénarios, prévisions) pour repartir à zéro.
                  Cette action est irréversible et supprimera définitivement toutes vos données.
                </p>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-all"
                >
                  <Trash2 size={18} />
                  Supprimer toutes mes données
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-white border border-red-300 rounded-lg p-4">
                  <p className="font-semibold text-red-900 mb-2">
                    Êtes-vous absolument sûr ?
                  </p>
                  <p className="text-sm text-red-800">
                    Cette action supprimera définitivement toutes vos données. Cette action ne peut pas être annulée.
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={handleDeleteTestData}
                    disabled={deleteLoading}
                    className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Trash2 size={18} />
                    {deleteLoading ? 'Suppression...' : 'Oui, supprimer toutes mes données'}
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    disabled={deleteLoading}
                    className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-all disabled:opacity-50"
                  >
                    Annuler
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
